import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  Clock,
  AlertCircle,
  Calendar,
  Flag,
  Bell,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { formatDeDate, isoToDateInput, dateInputToISO } from "@/lib/dateFormat";
import { PageHeader, PrimaryButton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const CATEGORIES = [
  {
    key: "aktiv",
    title: "Diese Woche aktiv angehen",
    color: "bg-emerald-50 border-emerald-200",
    dot: "bg-emerald-500",
    text: "text-emerald-700",
    icon: Flag,
  },
  {
    key: "wartet",
    title: "Wartet auf andere",
    color: "bg-amber-50 border-amber-200",
    dot: "bg-amber-500",
    text: "text-amber-700",
    icon: Clock,
  },
  {
    key: "ruhend",
    title: "Bewusst ruhen",
    color: "bg-gray-100 border-gray-200",
    dot: "bg-gray-400",
    text: "text-gray-600",
    icon: CheckCircle2,
  },
];

const AMPEL_DOT = { gruen: "bg-emerald-500", gelb: "bg-amber-400", rot: "bg-rose-500" };

function AmpelBadge({ status }) {
  return <span className={`inline-block w-2 h-2 rounded-full ${AMPEL_DOT[status] || "bg-gray-400"}`} />;
}

export default function WeeklyPlanning() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pendingRuhend, setPendingRuhend] = useState(null); // { challenge_id, current_termin }
  const [closeDialog, setCloseDialog] = useState(false);
  const [closeNotes, setCloseNotes] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["weekly-planning"],
    queryFn: async () => (await api.get(`/weekly-planning`)).data,
  });

  const setCategoryMut = useMutation({
    mutationFn: async ({ challengeId, category, naechster_termin }) => {
      const payload = { category, sync_challenge_status: true };
      if (naechster_termin) payload.naechster_termin = naechster_termin;
      return (await api.put(`/weekly-planning/${challengeId}`, payload)).data;
    },
    onSuccess: () => {
      toast.success("Kategorie gespeichert");
      qc.invalidateQueries({ queryKey: ["weekly-planning"] });
      qc.invalidateQueries({ queryKey: ["challenges"] });
      qc.invalidateQueries({ queryKey: ["my-day"] });
      setPendingRuhend(null);
    },
    onError: (err) => {
      const msg = formatApiError(err);
      // If ruhend requires date, open dialog
      if (msg && msg.includes("Bewusst ruhen")) {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    },
  });

  const reorderMut = useMutation({
    mutationFn: async ({ challengeId, direction }) => {
      return (await api.put(`/weekly-planning/${challengeId}/reorder`, { direction })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["weekly-planning"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const closeMut = useMutation({
    mutationFn: async (notes) => {
      return (await api.post(`/weekly-planning/close`, { notes: notes || null })).data;
    },
    onSuccess: () => {
      toast.success("Wochenplanung gespeichert");
      qc.invalidateQueries({ queryKey: ["weekly-planning"] });
      setCloseDialog(false);
      setCloseNotes("");
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const setFollowUpCategoryMut = useMutation({
    mutationFn: async ({ followupId, category, naechster_termin }) => {
      const payload = { category };
      if (naechster_termin) payload.naechster_termin = naechster_termin;
      return (await api.put(`/weekly-planning/followups/${followupId}`, payload)).data;
    },
    onSuccess: () => {
      toast.success("Wiedervorlage eingeordnet");
      qc.invalidateQueries({ queryKey: ["weekly-planning"] });
      qc.invalidateQueries({ queryKey: ["my-day"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const resetFollowUpCategoryMut = useMutation({
    mutationFn: async (followupId) => {
      return (await api.delete(`/weekly-planning/followups/${followupId}`)).data;
    },
    onSuccess: () => {
      toast.success("Wiedervorlage zurückgesetzt");
      qc.invalidateQueries({ queryKey: ["weekly-planning"] });
      qc.invalidateQueries({ queryKey: ["my-day"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const [pendingFuRuhend, setPendingFuRuhend] = useState(null); // { followupId, title }

  const grouped = useMemo(() => {
    const g = { aktiv: [], wartet: [], ruhend: [] };
    (data?.items || []).forEach((it) => {
      if (!g[it.category]) g[it.category] = [];
      g[it.category].push(it);
    });
    // sort each by sort_order
    Object.keys(g).forEach((k) => g[k].sort((a, b) => a.sort_order - b.sort_order));
    return g;
  }, [data]);

  function handleCategoryChange(item, targetCat) {
    if (item.category === targetCat) return;
    if (targetCat === "ruhend" && !item.challenge?.naechster_termin) {
      // Prompt for new date
      setPendingRuhend({ challengeId: item.challenge_id, titel: item.challenge?.titel });
      return;
    }
    setCategoryMut.mutate({ challengeId: item.challenge_id, category: targetCat });
  }

  function handleReorder(item, direction) {
    reorderMut.mutate({ challengeId: item.challenge_id, direction });
  }

  if (isLoading || !data) {
    return <div className="p-8 text-sm text-gray-500" data-testid="weekly-planning-loading">Lädt Wochenplanung…</div>;
  }

  const counts = { aktiv: grouped.aktiv.length, wartet: grouped.wartet.length, ruhend: grouped.ruhend.length };
  const isClosed = data.closed;

  return (
    <div data-testid="weekly-planning-page" className="space-y-6">
      <PageHeader
        title="Wochenplanung"
        subtitle={
          <span>
            Kalenderwoche <strong data-testid="weekly-planning-kw">{data.iso_week}</strong>
            {" "}({formatDeDate(data.week_start)} – {formatDeDate(data.week_end)})
            {isClosed ? (
              <span
                className="ml-3 inline-block text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full"
                data-testid="weekly-planning-status-badge"
              >
                Zuletzt dokumentiert am {formatDeDate(data.close_log.closed_at)}
              </span>
            ) : null}
          </span>
        }
        testid="weekly-planning-header"
        action={
          <PrimaryButton
            onClick={() => {
              setCloseNotes("");
              setCloseDialog(true);
            }}
            data-testid="weekly-planning-close-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" /> {isClosed ? "Wochenplanung aktualisieren" : "Wochenplanung abschließen"}
          </PrimaryButton>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {CATEGORIES.map((cat) => {
          const items = grouped[cat.key] || [];
          return (
            <section
              key={cat.key}
              className={`rounded-xl border p-4 ${cat.color}`}
              data-testid={`weekly-planning-col-${cat.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <cat.icon className={`w-4 h-4 ${cat.text}`} />
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-800">{cat.title}</h2>
                </div>
                <div className={`text-xs font-semibold ${cat.text}`}>{items.length}</div>
              </div>

              {items.length === 0 ? (
                <div className="text-xs text-gray-500 italic text-center py-8">Keine Einträge.</div>
              ) : (
                <div className="space-y-3">
                  {items.map((item, idx) => (
                    <PlanningCard
                      key={item.id}
                      item={item}
                      onCategoryChange={(target) => handleCategoryChange(item, target)}
                      onReorder={(dir) => handleReorder(item, dir)}
                      isFirst={idx === 0}
                      isLast={idx === items.length - 1}
                      onOpenContact={(cid) => navigate(`/contacts/${cid}`)}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* NORA Phase 4: Wiedervorlagen dieser Woche */}
      <FollowUpsWeekSection
        block={data.followups}
        onCategorize={(followupId, category) => {
          if (category === "ruhend") {
            const item =
              (data.followups?.pending || []).find((p) => p.followup_id === followupId) ||
              (data.followups?.planned || []).find((p) => p.followup_id === followupId);
            setPendingFuRuhend({ followupId, title: item?.title || "Wiedervorlage" });
            return;
          }
          setFollowUpCategoryMut.mutate({ followupId, category });
        }}
        onReset={(followupId) => resetFollowUpCategoryMut.mutate(followupId)}
        onOpenContact={(cid) => navigate(`/contacts/${cid}`)}
      />

      {/* Ruhend Wiedervorlage prompt (Followups Phase 4) */}
      <FollowUpRuhendPrompt
        pending={pendingFuRuhend}
        onCancel={() => setPendingFuRuhend(null)}
        onConfirm={(dateStr) => {
          setFollowUpCategoryMut.mutate({
            followupId: pendingFuRuhend.followupId,
            category: "ruhend",
            naechster_termin: dateStr,
          });
          setPendingFuRuhend(null);
        }}
      />
      <RuhendPrompt
        pending={pendingRuhend}
        onCancel={() => setPendingRuhend(null)}
        onConfirm={(dateStr) =>
          setCategoryMut.mutate({
            challengeId: pendingRuhend.challengeId,
            category: "ruhend",
            naechster_termin: dateStr,
          })
        }
      />

      {/* Close dialog */}
      <Dialog
        open={closeDialog}
        onOpenChange={(open) => {
          if (open) setCloseNotes("");
          setCloseDialog(open);
        }}
      >
        <DialogContent data-testid="weekly-planning-close-dialog">
          <DialogHeader>
            <DialogTitle>{isClosed ? "Wochenplanung aktualisieren" : "Wochenplanung abschließen"}</DialogTitle>
            <DialogDescription>
              KW {data.iso_week} · {counts.aktiv} aktiv · {counts.wartet} wartet · {counts.ruhend} ruhend
              {isClosed ? (
                <span className="block mt-1 text-xs text-gray-500">
                  Speichert einen weiteren Eintrag im Wochenverlauf. Ältere Einträge bleiben erhalten.
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Notiz (optional)</Label>
            <Textarea
              value={closeNotes}
              onChange={(e) => setCloseNotes(e.target.value)}
              rows={4}
              placeholder="Kurze Notiz zur Woche – was war wichtig, was hast du gelernt?"
              data-testid="weekly-planning-close-notes"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialog(false)} data-testid="weekly-planning-close-cancel">
              Abbrechen
            </Button>
            <Button
              className="bg-black text-white hover:bg-gray-800"
              onClick={() => closeMut.mutate(closeNotes)}
              disabled={closeMut.isPending}
              data-testid="weekly-planning-close-confirm"
            >
              {closeMut.isPending ? "Speichern…" : (isClosed ? "Wochenplanung aktualisieren" : "Wochenplanung abschließen")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {data.close_logs && data.close_logs.length > 0 ? (
        <div className="crm-card p-4" data-testid="weekly-planning-close-log">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-gray-800 text-sm">Verlauf der Wochenabschlüsse</span>
            <span className="text-xs text-gray-500">({data.close_logs.length})</span>
          </div>
          <ol className="space-y-3">
            {data.close_logs.map((log, idx) => (
              <li key={log.id} className="text-sm" data-testid={`weekly-planning-close-log-entry-${idx}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{formatDeDate(log.closed_at)}</span>
                  {idx === 0 ? (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Neuester</span>
                  ) : null}
                  <span className="text-xs text-gray-500">
                    {log.count_aktiv} aktiv · {log.count_wartet} wartet · {log.count_ruhend} ruhend
                  </span>
                </div>
                {log.notes ? <p className="mt-1 text-sm text-gray-700 whitespace-pre-line">{log.notes}</p> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

function PlanningCard({ item, onCategoryChange, onReorder, isFirst, isLast, onOpenContact }) {
  const ch = item.challenge || {};
  const overdue = ch.naechster_termin && new Date(ch.naechster_termin) < new Date();
  return (
    <div className="rounded-lg bg-white border border-gray-200 p-3" data-testid={`weekly-planning-item-${item.challenge_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-gray-900 leading-tight">{ch.titel || "—"}</div>
          {ch.ziel ? <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{ch.ziel}</p> : null}
        </div>
        <div className="flex flex-col items-center gap-1 shrink-0">
          <button
            onClick={() => onReorder("up")}
            disabled={isFirst}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`weekly-planning-up-${item.challenge_id}`}
            aria-label="höher"
          >
            <ArrowUp className="w-3.5 h-3.5 text-gray-500" />
          </button>
          <button
            onClick={() => onReorder("down")}
            disabled={isLast}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`weekly-planning-down-${item.challenge_id}`}
            aria-label="niedriger"
          >
            <ArrowDown className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Kategorien-Buttons */}
      <div className="flex flex-wrap gap-1 mt-3" data-testid={`weekly-planning-cat-buttons-${item.challenge_id}`}>
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            onClick={() => onCategoryChange(cat.key)}
            className={`text-[11px] px-2 py-1 rounded-md border ${
              item.category === cat.key
                ? "bg-gray-900 text-white border-gray-900"
                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
            }`}
            data-testid={`weekly-planning-cat-${item.challenge_id}-${cat.key}`}
          >
            {cat.title}
          </button>
        ))}
      </div>

      {/* Zusatzinfos für "wartet auf andere" */}
      {item.category === "wartet" ? (
        <div className="mt-3 pt-3 border-t border-gray-100 text-xs space-y-1">
          {ch.naechster_schritt ? (
            <div>
              <span className="uppercase text-[10px] tracking-wider text-gray-500 mr-1">Zusage:</span>
              <span className="text-gray-700">{ch.naechster_schritt}</span>
            </div>
          ) : null}
          {ch.naechster_termin ? (
            <div className={overdue ? "text-rose-700 font-medium" : "text-gray-600"}>
              Termin: {formatDeDate(ch.naechster_termin)}{overdue ? " · überfällig" : ""}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Beteiligte */}
      {ch.contacts && ch.contacts.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1">
          {ch.contacts.slice(0, 4).map((link) => (
            <button
              key={link.id}
              onClick={() => link.contact_id && onOpenContact?.(link.contact_id)}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-50 hover:bg-gray-100 border border-gray-200"
            >
              <AmpelBadge status={link.ampel_status} />
              {link.contact_name}
            </button>
          ))}
          {ch.contacts.length > 4 ? <span className="text-xs text-gray-400">+{ch.contacts.length - 4}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function RuhendPrompt({ pending, onCancel, onConfirm }) {
  const [dateVal, setDateVal] = useState(isoToDateInput(new Date().toISOString()));
  return (
    <Dialog open={!!pending} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid="weekly-planning-ruhend-dialog">
        <DialogHeader>
          <DialogTitle>Neue Wiedervorlage nötig</DialogTitle>
          <DialogDescription>
            Für „Bewusst ruhen“ muss ein neues Wiedervorlage-Datum gesetzt sein.
            {pending?.titel ? <span className="block mt-1 text-xs text-gray-500">Herausforderung: {pending.titel}</span> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Wiedervorlage</Label>
          <Input
            type="date"
            value={dateVal}
            onChange={(e) => setDateVal(e.target.value)}
            data-testid="weekly-planning-ruhend-date"
          />
          <p className="text-xs text-gray-500">Nur ein Datum – Uhrzeit nicht nötig.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="weekly-planning-ruhend-cancel">Abbrechen</Button>
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => dateVal && onConfirm(dateVal)}
            data-testid="weekly-planning-ruhend-confirm"
          >
            Auf „Bewusst ruhen“ setzen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -------- NORA Phase 4: Wiedervorlagen dieser Woche ----------

function FollowUpsWeekSection({ block, onCategorize, onReset, onOpenContact }) {
  const pending = block?.pending || [];
  const planned = block?.planned || [];
  const total = pending.length + planned.length;
  return (
    <section
      className="crm-card p-5 border-blue-100 bg-blue-50/30"
      data-testid="weekly-planning-followups-section"
    >
      <div className="flex items-center gap-2 mb-2">
        <Bell className="w-4 h-4 text-blue-700" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-800">
          Wiedervorlagen dieser Woche
        </h2>
        <span className="text-xs text-gray-500">({total})</span>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Offene Wiedervorlagen dieser KW, die (noch) nicht an eine Herausforderung geknüpft sind.
        Einordnung ändert nur die Wochenplanung – es entsteht keine Herausforderung.
      </p>

      {/* Noch nicht eingeplant */}
      <div className="mb-4" data-testid="weekly-planning-followups-pending">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 mb-2">
          Noch nicht eingeplant · {pending.length}
        </div>
        {pending.length === 0 ? (
          <div className="text-xs text-gray-500 italic px-2 py-4 text-center border border-dashed border-blue-200 rounded-md bg-white/60">
            Alle Wiedervorlagen dieser Woche sind eingeordnet.
          </div>
        ) : (
          <ul className="space-y-2">
            {pending.map((it) => (
              <FollowUpRow
                key={it.followup_id}
                item={it}
                onCategorize={(cat) => onCategorize(it.followup_id, cat)}
                onOpenContact={onOpenContact}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Bereits eingeordnet */}
      {planned.length > 0 ? (
        <div data-testid="weekly-planning-followups-planned">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 mb-2">
            Bereits eingeordnet · {planned.length}
          </div>
          <ul className="space-y-2">
            {planned.map((it) => (
              <FollowUpRow
                key={it.followup_id}
                item={it}
                onCategorize={(cat) => onCategorize(it.followup_id, cat)}
                onReset={() => onReset(it.followup_id)}
                onOpenContact={onOpenContact}
              />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

const FU_CATS = [
  { key: "aktiv", label: "Diese Woche aktiv angehen", ring: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { key: "wartet", label: "Wartet auf andere", ring: "bg-amber-100 text-amber-800 border-amber-300" },
  { key: "ruhend", label: "Bewusst ruhen", ring: "bg-gray-100 text-gray-700 border-gray-300" },
];

function FollowUpRow({ item, onCategorize, onReset, onOpenContact }) {
  return (
    <li
      className="bg-white rounded-lg border border-gray-200 p-3"
      data-testid={`weekly-planning-followup-${item.followup_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900 leading-tight">{item.title}</span>
            <span className="text-[11px] text-gray-500">· {formatDeDate(item.due_date)}</span>
          </div>
          {item.contact_name ? (
            <button
              type="button"
              onClick={() => item.contact_id && onOpenContact?.(item.contact_id)}
              className="mt-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-50 hover:bg-gray-100 border border-gray-200"
              data-testid={`weekly-planning-followup-contact-${item.followup_id}`}
            >
              <AmpelBadge status={item.contact_ampel_status} />
              {item.contact_name}
            </button>
          ) : null}
        </div>
        {onReset ? (
          <button
            type="button"
            onClick={onReset}
            className="p-1 text-gray-400 hover:text-blue-700"
            title="Einordnung zurücksetzen"
            data-testid={`weekly-planning-followup-reset-${item.followup_id}`}
            aria-label="Zurücksetzen"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>
      <div className="mt-2 flex flex-wrap gap-1" data-testid={`weekly-planning-followup-cat-${item.followup_id}`}>
        {FU_CATS.map((cat) => {
          const isSel = item.category === cat.key;
          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => onCategorize(cat.key)}
              className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
                isSel ? cat.ring + " font-semibold" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
              data-testid={`weekly-planning-followup-cat-${item.followup_id}-${cat.key}`}
            >
              {cat.label}
            </button>
          );
        })}
      </div>
    </li>
  );
}

function FollowUpRuhendPrompt({ pending, onCancel, onConfirm }) {
  const [dateVal, setDateVal] = useState(isoToDateInput(new Date().toISOString()));
  return (
    <Dialog open={!!pending} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid="weekly-planning-followup-ruhend-dialog">
        <DialogHeader>
          <DialogTitle>Neue Wiedervorlage nötig</DialogTitle>
          <DialogDescription>
            Für „Bewusst ruhen“ muss ein neues Wiedervorlage-Datum gesetzt sein. Die Wiedervorlage wird auf dieses
            Datum verschoben.
            {pending?.title ? <span className="block mt-1 text-xs text-gray-500">Wiedervorlage: {pending.title}</span> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-gray-600">Neues Wiedervorlage-Datum</Label>
          <Input
            type="date"
            value={dateVal}
            onChange={(e) => setDateVal(e.target.value)}
            data-testid="weekly-planning-followup-ruhend-date"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="weekly-planning-followup-ruhend-cancel">Abbrechen</Button>
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => dateVal && onConfirm(dateVal)}
            data-testid="weekly-planning-followup-ruhend-confirm"
          >
            Wiedervorlage verschieben und ruhen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
